<template>
  <section class="programs">
    <div class="container programs__inner">
      <div class="programs__content">
        <h2 class="programs__title">{{ t('programs.title') }}</h2>
        <p class="programs__text">{{ t('programs.desc1') }}</p>
        <p class="programs__text">{{ t('programs.desc2') }}</p>
        <NuxtLink
          :to="localePath('/recherche/etablissement')"
          class="btn programs__cta">
          {{ t('programs.cta') }}
        </NuxtLink>
      </div>

      <div
        v-if="backSchool || frontSchool"
        class="programs__visual">
        <ShowcaseSchoolCard
          v-if="backSchool"
          :school="backSchool"
          variant="back" />
        <ShowcaseSchoolCard
          v-if="frontSchool"
          :school="frontSchool"
          variant="front" />
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import type { ComputedRef, PropType } from 'vue'
import type { ShowcaseSchoolCardData } from '~/types/homeShowcases'
import ShowcaseSchoolCard from '~/components/cards/ShowcaseSchoolCard.vue'

const props = defineProps({
  schools: {
    type: Array as PropType<readonly ShowcaseSchoolCardData[]>,
    default: () => [],
  },
})

const { t } = useI18n()
const localePath = useLocalePath()

const backSchool: ComputedRef<ShowcaseSchoolCardData | undefined> = computed(
  (): ShowcaseSchoolCardData | undefined => props.schools[0],
)

const frontSchool: ComputedRef<ShowcaseSchoolCardData | undefined> = computed(
  (): ShowcaseSchoolCardData | undefined => props.schools[1],
)
</script>

<style scoped>
.programs {
  background: var(--bg-base);
  padding-block: var(--space-20);
  overflow: hidden;
}

.programs__inner {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-16);
  align-items: center;
}

.programs__content {
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
}

.programs__title {
  font-size: clamp(1.75rem, 3.5vw, 2.5rem);
  font-weight: 800;
  line-height: var(--leading-tight);
  letter-spacing: var(--tracking-tight);
  color: var(--text-primary);
}

.programs__text {
  font-size: var(--text-base);
  color: var(--text-secondary);
  line-height: var(--leading-relaxed);
}

.programs__cta {
  width: fit-content;
  background: var(--accent-orange);
  color: #fff;
  font-weight: 700;
  font-size: var(--text-base);
  padding: 0.75em 2em;
  border-radius: var(--radius-md);
  border: none;
  cursor: pointer;
  transition: filter var(--duration-fast) var(--ease-out);
  margin-top: var(--space-2);
}

.programs__cta:hover {
  filter: brightness(1.1);
}

.programs__visual {
  position: relative;
  height: 480px;
}

@media (max-width: 900px) {
  .programs__inner {
    grid-template-columns: 1fr;
    gap: var(--space-10);
  }

  .programs__visual {
    height: 360px;
    max-width: 420px;
    margin-inline: auto;
    width: 100%;
  }

  .programs__title {
    font-size: clamp(1.5rem, 6vw, 2rem);
  }

  .programs__cta {
    width: 100%;
    text-align: center;
    justify-content: center;
  }
}

@media (max-width: 480px) {
  .programs__visual {
    height: 300px;
  }
}
</style>
